import allGlobalPromosGql from '~/gql/promo.gql.js'
import useDatoQuery from '~/composables/useDatoQuery'

import { parseMessage, type SpecialTheme, type DatoPromotion, type PromoBanner } from '~/assets/js/helpers/promo'

export const usePromoStore = defineStore('promo', {
  state: () => {
    return {
      theme: {} as SpecialTheme,
      banner: {} as PromoBanner,
      tagText: '',
      styles: '',
      promos: [] as DatoPromotion[],
      showPolarisedTag: false
    }
  },

  actions: {
    async setActiveGlobalPromoTheme(themedPromo: DatoPromotion) {
      this.theme = {
        ...themedPromo.theme,
        shopifyPromoIds: themedPromo.shopifyPromotionId.split(',')
      }

      this.showPolarisedTag = themedPromo.showPolarisedTag

      this.banner = {
        message: parseMessage(themedPromo.bannerMessage),
        mobileMessage: parseMessage(themedPromo.mobileBannerMessage),
        link: themedPromo.bannerLink
      }

      const styles = Object.entries(this.theme).map(
        ([key, colours]: [string, any]) => {
          const excludedKeys = ['id', 'shopifyPromoId', 'type', 'shopifyPromoIds'];
          if (!excludedKeys.includes(key) && colours) {
            const formattedKey = key.replace('Colour', '').replace(/([A-Z])/g, upperCase => `-${upperCase.toLowerCase()}`);
            const prependedKey = `promo-${formattedKey}`
            const style = `--${prependedKey}: rgba(${colours.red}, ${colours.green}, ${colours.blue}, var(--${prependedKey}-alpha-override, 1));`;
    
            return style;
          }
        }
      )
      this.styles = styles.join('')

      this.tagText = themedPromo.productTagText
    },

    async getActiveGlobalPromos() {
      const { $sentry } = useNuxtApp()
      try {
        const query = allGlobalPromosGql
        const options = {
          query: query,
          prefetchedRequest: 'allGlobalPromos'
        }
        const { data } = await useDatoQuery(
          options
        )

        const globalPromos = data.value.allGlobalPromos || null
        if (globalPromos) {
          this.promos = globalPromos
        }
      } catch (error) {
        $sentry.captureException(error)
      }
    },

    async checkGlobalPromoIsActive() {
      if (this.promos) {
        const shopifyStore = useShopifyStore()

        // Add the promo data to each automatic discount for each promo and discount id
        for (const promo of this.promos) {
          try {
            await shopifyStore.addGlobalPromoData(promo)
          } catch (error) {
            const { $sentry } = useNuxtApp()
            $sentry.captureException(error)
          }
        }
        
        // Get the first special themed promo in case there are accidentally multiple
        const themedPromo = this.promos.filter(promo => promo.theme?.type === 'special')[0]

        if (themedPromo && themedPromo.shopifyPromotionId) {
          const themedPromoIds = themedPromo.shopifyPromotionId.split(',')

          const hasActiveDiscount = themedPromoIds.some(id => {
            return shopifyStore.checkActivePromoById(id)
          })
  
          if (hasActiveDiscount) {
            await this.setActiveGlobalPromoTheme(themedPromo)
          }
        }
      }
    }
  }
})
